import type { Building } from "building-types/building/buildingTypes";
import type { BuildingGeometry, BuildingGeometryEmitter } from "building-types/geometry/buildingGeometryTypes";
import type { BuildingProp } from "building-types/geometry/buildingGeometryTypes";
import { FaceCategory, type FaceGroupName } from "building-types/materials/faceCategories";
import { LRUStringCache } from "common/utils/lruStringCache";
import type { AABB3 } from "geom/aabb3Types";
import { geomAABB3 } from "geom/geom";

/**
 * Emitter decorator that reports geometry carrying non-finite (NaN) coordinates.
 *
 * Degenerate generation — normalizing a zero-length vector, most often — produces faces whose
 * vertices are NaN. They are invisible when rendered, so nothing surfaces them, but they poison any
 * AABB derived from the element and silently corrupt interior/exterior classification. Baselines
 * then record them as `null` and the defect persists unnoticed.
 *
 * This observes only: geometry is forwarded unchanged, so it never alters output or baselines.
 * Detection is by AABB because that is precisely the property a NaN vertex destroys.
 */
/**
 * `facesAABB3` returns undefined for an empty face group despite its AABB3 return annotation, and
 * empty groups are routine, so the bounds genuinely have to be treated as optional here.
 */
function hasNonFiniteBounds(aabb: AABB3 | undefined): boolean {
  if (aabb === undefined) {
    return false;
  }

  return (
    !Number.isFinite(aabb.minX) ||
    !Number.isFinite(aabb.minY) ||
    !Number.isFinite(aabb.minZ) ||
    !Number.isFinite(aabb.maxX) ||
    !Number.isFinite(aabb.maxY) ||
    !Number.isFinite(aabb.maxZ)
  );
}

/**
 * Cap on retained fingerprints. Bounded because this is process-lifetime state in long-lived
 * server processes; the oldest building's fingerprint simply becomes eligible to warn again.
 */
const MAX_REPORTED_FINGERPRINTS = 256;

/**
 * Building-scoped category-count fingerprints already warned about. The detector is rebuilt per
 * `getBuildingGeometries` call, so without this a steady-state defect logs on every rebuild.
 */
const reportedFingerprints = new LRUStringCache<true>(MAX_REPORTED_FINGERPRINTS);

export class NonFiniteGeometryDetector implements BuildingGeometryEmitter {
  private readonly counts = new Map<FaceCategory, number>();

  constructor(
    private readonly inner: BuildingGeometryEmitter,
    private readonly building: Pick<Building, "name" | "docHash">,
  ) {}

  emit = (geometry: BuildingGeometry): void => {
    if (hasNonFiniteBounds(geomAABB3(geometry.geom))) {
      this.counts.set(geometry.faceCategory, (this.counts.get(geometry.faceCategory) ?? 0) + 1);
    }

    this.inner.emit(geometry);
  };

  emitProp = (prop: BuildingProp): void => {
    this.inner.emitProp?.(prop);
  };

  beginGroup = (name: FaceGroupName, sourceId?: string): void => {
    this.inner.beginGroup?.(name, sourceId);
  };

  endGroup = (name: FaceGroupName): void => {
    this.inner.endGroup?.(name);
  };

  /**
   * Reports once per building so a widespread defect does not produce one message per face, and
   * once per building/category-count fingerprint so rebuilds of the same defect stay quiet.
   */
  report(): void {
    if (this.counts.size === 0) {
      return;
    }

    const byCategory = [...this.counts.entries()]
      .sort((a, b) => b[1] - a[1])
      // FaceCategory is a numeric enum; print the name so the warning is readable.
      .map(([category, count]) => `${FaceCategory[category] ?? category}=${count}`)
      .join(", ");

    // Scoped by `docHash` so each affected building is counted separately: a global
    // category-count key would silence every other home sharing a fingerprint like `CASEWORK=1`.
    const fingerprint = `${this.building.docHash}|${byCategory}`;
    if (reportedFingerprints.has(fingerprint)) {
      return;
    }
    reportedFingerprints.add(fingerprint, true);

    const total = [...this.counts.values()].reduce((sum, count) => sum + count, 0);

    // Deliberately a console warning rather than Sentry: ~43% of production homes currently trip
    // this, so reporting it as an exception would drown the issue stream. Route it to Sentry once
    // the known producers are fixed and the volume is near zero.
    console.warn(
      `Building geometry for "${this.building.name}" (doc ${this.building.docHash}) emitted ` +
        `${total} face(s) with non-finite coordinates (${byCategory}). These are invisible when ` +
        `rendered but poison any AABB derived from them.`,
    );
  }

  /** Test seam: the fingerprint cache is module state and would otherwise leak between test cases. */
  static resetReportHistory(): void {
    reportedFingerprints.clear();
  }
}

/**
 * The detector's per-face AABB check is too costly to pay in production's hot path, so it only
 * runs outside production. Keep this on in dev/staging/test, where the volume is far lower and
 * catching new non-finite producers matters more than the extra cost.
 */
const NON_FINITE_DETECTION_ENABLED = process.env.NODE_ENV !== "production";

/**
 * Wraps `emitter` with a {@link NonFiniteGeometryDetector} outside production; in production
 * returns `emitter` unwrapped so callers pay none of the detection cost.
 */
export function wrapWithNonFiniteDetection(
  emitter: BuildingGeometryEmitter,
  building: Pick<Building, "name" | "docHash">,
): { emitter: BuildingGeometryEmitter; report: () => void } {
  if (!NON_FINITE_DETECTION_ENABLED) {
    return { emitter, report: () => {} };
  }

  const detector = new NonFiniteGeometryDetector(emitter, building);
  return { emitter: detector, report: () => detector.report() };
}
